/**
 * FlowLifecycleContext — thin React context exposing widget-state
 * read/write so any widget rendered inside `<FlowLifecycleManager>`
 * can opt into server-persisted state via `useWidgetState()`.
 *
 * This is intentionally a separate file from FlowLifecycleManager so
 * widgets can `import { useWidgetState }` without pulling in the
 * (heavier) manager component.
 */

import { createContext, useContext } from 'react';

export interface FlowLifecycleContextValue {
  /**
   * Current widget_states snapshot. Keyed "<chatId>::<widgetName>".
   * Updated reactively when the manager refetches the guide state.
   */
  widgetStates: Record<string, Record<string, unknown>>;
  /**
   * Patch a widget's state. Debounced + queued internally — the
   * caller can fire this freely without thinking about network.
   *
   * Returns a Promise that resolves when the patch reaches the server.
   * (Most callers ignore the promise — useWidgetState wraps this.)
   */
  patchWidgetState: (widgetKey: string, partial: Record<string, unknown>) => Promise<void>;
  /**
   * The current flow id (so widgets that need it for telemetry can
   * pull it without prop-drilling).
   */
  flowId: string;
  /**
   * Register a per-widget flush function. Each `useWidgetState` instance
   * calls this on mount with a function that synchronously sends any
   * pending debounced patch. The manager calls all registered fns in
   * parallel from `flushPendingWidgetPatches()` — used right before
   * the atomic `finalize` request so the final widgetStates snapshot on
   * the server matches what the user just submitted.
   *
   * Returns the unregister fn (call from the widget's unmount cleanup).
   */
  registerFlush: (flush: () => Promise<void>) => () => void;
  /**
   * Drain every registered widget's pending debounced patch. Returns
   * once all in-flight PATCH requests have settled. Hosts call this
   * before `finalize` to guarantee the server has the final widget
   * state before status flips to 'completed'.
   */
  flushPendingWidgetPatches: () => Promise<void>;
  /**
   * Live count of widgets that have called `useWidgetState`'s
   * register API. Used by the leave guard to decide whether to await
   * a flush before letting the user navigate. >0 means at least one
   * widget could have a debounced patch in flight.
   */
  registeredWidgetCount: number;
}

export const FlowLifecycleContext = createContext<FlowLifecycleContextValue | null>(null);

/**
 * Read the manager context. Returns null when the calling widget is
 * mounted OUTSIDE a `<FlowLifecycleManager>` — used by `useWidgetState`
 * to fall back to local-state-only mode (legacy paths still work).
 */
export function useFlowLifecycleContext(): FlowLifecycleContextValue | null {
  return useContext(FlowLifecycleContext);
}
